import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import api from "../utils/api";

const GOOGLE_SCRIPT_ID = "google-identity-services";
const DIRECT_ADMIN_EMAIL = "admin123@quantumlogics.io";
const DIRECT_ADMIN_PASSWORD = "admin123";

const loadGoogleIdentityScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();

    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

const isDirectAdminCredentials = (email, password) =>
  email.trim().toLowerCase() === DIRECT_ADMIN_EMAIL &&
  password === DIRECT_ADMIN_PASSWORD;

// ── OTP Input: 6 individual digit boxes ───────────────────────────────────────
function OtpInput({ value, onChange, disabled }) {
  const inputs = useRef([]);

  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleChange = (i, e) => {
    const ch = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = ch;
    const joined = next.join("");
    onChange(joined);
    if (ch && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
      const next = [...digits];
      next[i - 1] = "";
      onChange(next.join(""));
    }
    if (e.key === "ArrowLeft" && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    onChange(pasted.padEnd(6, "").slice(0, 6));
    const focusIdx = Math.min(pasted.length, 5);
    inputs.current[focusIdx]?.focus();
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        justifyContent: "center",
        margin: "20px 0",
      }}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={d}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          style={{
            width: 44,
            height: 54,
            textAlign: "center",
            fontSize: "1.375rem",
            fontWeight: 700,
            fontFamily: "var(--display)",
            background: d ? "rgba(79,142,247,0.08)" : "var(--bg-1)",
            border: `2px solid ${d ? "rgba(79,142,247,0.5)" : "var(--line-2)"}`,
            borderRadius: 10,
            color: "var(--text-1)",
            outline: "none",
            transition: "border-color .15s, background .15s",
            caretColor: "var(--blue)",
            marginRight: i === 2 ? 12 : 0,
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "var(--blue)";
            e.target.style.boxShadow = "0 0 0 3px rgba(79,142,247,.15)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = d
              ? "rgba(79,142,247,0.5)"
              : "var(--line-2)";
            e.target.style.boxShadow = "none";
          }}
        />
      ))}
    </div>
  );
}

// ── Countdown timer for resend cooldown ───────────────────────────────────────
function useCountdown(initial) {
  const [count, setCount] = useState(initial);
  const timerRef = useRef(null);

  const start = useCallback((seconds) => {
    setCount(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCount((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  return [count, start];
}

// ── Forgot Password multi-step panel ──────────────────────────────────────────
// Steps: "email" → "otp" → "newpass" → "done"
function ForgotPasswordPanel({ onBack, notify }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, startCountdown] = useCountdown(0);

  // ── Step 1: Request OTP ─────────────────────────────────────────────────────
  const requestOtp = async (isResend = false) => {
    setError("");
    if (!email.trim()) return setError("Please enter your email address.");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      if (!isResend) setStep("otp");
      startCountdown(60);
      if (isResend) notify("New OTP sent to your email.");
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to send OTP.";
      const retryAfter = err.response?.data?.retryAfter;
      if (retryAfter) startCountdown(retryAfter);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ─────────────────────────────────────────────────────
  const verifyOtp = async () => {
    setError("");
    if (otp.replace(/\D/g, "").length < 6)
      return setError("Please enter the complete 6-digit code.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        email: email.trim(),
        otp: otp.replace(/\D/g, ""),
      });
      setResetToken(data.resetToken);
      setStep("newpass");
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed.");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Set new password ───────────────────────────────────────────────
  const resetPassword = async () => {
    setError("");
    if (!password) return setError("Please enter a new password.");
    if (password.length < 6)
      return setError("Password must be at least 6 characters.");
    if (password !== confirmPassword)
      return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { resetToken, password });
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const eyeIcon = (visible) =>
    visible ? (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ) : (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );

  // ── Step indicator ─────────────────────────────────────────────────────────
  const steps = ["email", "otp", "newpass"];
  const stepIdx = steps.indexOf(step);

  return (
    <>
      {/* Back button */}
      {step !== "done" && (
        <button
          onClick={
            step === "email"
              ? onBack
              : () => {
                  setStep(steps[Math.max(0, stepIdx - 1)]);
                  setError("");
                  setOtp("");
                }
          }
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--text-3)",
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: ".8125rem",
            fontWeight: 500,
            padding: "0 0 1rem 0",
            fontFamily: "var(--body)",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {step === "email" ? "Back to sign in" : "Back"}
        </button>
      )}

      {/* Step dots */}
      {step !== "done" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: "1.5rem",
          }}
        >
          {steps.map((s, i) => (
            <div
              key={s}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <div
                style={{
                  width: i < stepIdx ? 20 : i === stepIdx ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i <= stepIdx ? "var(--blue)" : "var(--line-2)",
                  transition: "all .3s",
                }}
              />
            </div>
          ))}
          <span
            style={{
              fontSize: ".72rem",
              color: "var(--text-3)",
              marginLeft: 4,
            }}
          >
            Step {stepIdx + 1} of 3
          </span>
        </div>
      )}

      {/* ── STEP: email ── */}
      {step === "email" && (
        <>
          <div className="auth-form-header">
            <h3 className="auth-form-title">Forgot password?</h3>
            <p className="auth-form-sub">
              Enter your account email and we'll send you a 6-digit verification
              code.
            </p>
          </div>
          <div className="auth-form-fields">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                className="form-input"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                autoFocus
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="none"
              />
            </div>
            {error && (
              <div className="auth-error">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}
            <button
              className="btn btn-primary auth-submit-btn"
              onClick={() => requestOtp(false)}
              disabled={loading}
            >
              {loading ? (
                <span className="auth-spinner" />
              ) : (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Send Verification Code
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* ── STEP: otp ── */}
      {step === "otp" && (
        <>
          <div className="auth-form-header">
            <h3 className="auth-form-title">Enter your code</h3>
            <p className="auth-form-sub">
              We sent a 6-digit code to{" "}
              <strong style={{ color: "var(--text-1)" }}>{email}</strong>.
              <br />
              It expires in 10 minutes. Check your spam folder if you do not
              see it.
            </p>
          </div>
          <OtpInput value={otp} onChange={setOtp} disabled={loading} />
          {error && (
            <div className="auth-error" style={{ marginBottom: ".875rem" }}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}
          <button
            className="btn btn-primary auth-submit-btn"
            onClick={verifyOtp}
            disabled={loading || otp.replace(/\D/g, "").length < 6}
          >
            {loading ? (
              <span className="auth-spinner" />
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Verify Code
              </>
            )}
          </button>
          <div
            style={{
              textAlign: "center",
              marginTop: ".875rem",
              fontSize: ".8125rem",
              color: "var(--text-3)",
            }}
          >
            Didn't receive it?{" "}
            {countdown > 0 ? (
              <span style={{ color: "var(--text-2)", fontWeight: 600 }}>
                Resend in {countdown}s
              </span>
            ) : (
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--blue)",
                  fontWeight: 600,
                  fontSize: ".8125rem",
                  fontFamily: "var(--body)",
                  padding: 0,
                }}
                onClick={() => requestOtp(true)}
                disabled={loading}
              >
                Resend code
              </button>
            )}
          </div>
        </>
      )}

      {/* ── STEP: newpass ── */}
      {step === "newpass" && (
        <>
          <div className="auth-form-header">
            <h3 className="auth-form-title">Set new password</h3>
            <p className="auth-form-sub">
              Choose a strong password. You'll use this to sign in from now on.
            </p>
          </div>
          <div className="auth-form-fields">
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="form-input-wrap">
                <input
                  className="form-input"
                  type={showPass ? "text" : "password"}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: 42 }}
                  autoFocus
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="form-eye-btn"
                  onClick={() => setShowPass((p) => !p)}
                  tabIndex={-1}
                >
                  {eyeIcon(showPass)}
                </button>
              </div>
              {/* Password strength bar */}
              {password && (
                <div style={{ marginTop: 6 }}>
                  <div
                    style={{
                      height: 3,
                      borderRadius: 2,
                      background: "var(--line-2)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        borderRadius: 2,
                        transition: "width .3s, background .3s",
                        width:
                          password.length >= 10
                            ? "100%"
                            : password.length >= 8
                              ? "66%"
                              : password.length >= 6
                                ? "33%"
                                : "10%",
                        background:
                          password.length >= 10
                            ? "var(--green)"
                            : password.length >= 8
                              ? "var(--amber)"
                              : password.length >= 6
                                ? "var(--amber)"
                                : "var(--red)",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: ".7rem",
                      color: "var(--text-3)",
                      marginTop: 3,
                    }}
                  >
                    Strength:{" "}
                    {password.length >= 10
                      ? "Strong"
                      : password.length >= 8
                        ? "Good"
                        : password.length >= 6
                          ? "Fair"
                          : "Weak"}
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <div className="form-input-wrap">
                <input
                  className="form-input"
                  type={showConfirmPass ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && resetPassword()}
                  style={{ paddingRight: 42 }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="form-eye-btn"
                  onClick={() => setShowConfirmPass((p) => !p)}
                  tabIndex={-1}
                >
                  {eyeIcon(showConfirmPass)}
                </button>
              </div>
              {confirmPassword && password && (
                <div
                  style={{
                    fontSize: ".7rem",
                    marginTop: 3,
                    color:
                      confirmPassword === password
                        ? "var(--green)"
                        : "var(--red)",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  {confirmPassword === password ? (
                    <>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>{" "}
                      Passwords match
                    </>
                  ) : (
                    <>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>{" "}
                      Passwords do not match
                    </>
                  )}
                </div>
              )}
            </div>
            {error && (
              <div className="auth-error">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}
            <button
              className="btn btn-primary auth-submit-btn"
              onClick={resetPassword}
              disabled={loading}
            >
              {loading ? (
                <span className="auth-spinner" />
              ) : (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Update Password
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* ── STEP: done ── */}
      {step === "done" && (
        <div style={{ textAlign: "center", padding: "1rem 0" }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(34,197,94,0.12)",
              border: "1px solid rgba(34,197,94,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1.375rem",
              color: "var(--green)",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.375rem",
              fontWeight: 800,
              letterSpacing: "-.025em",
              marginBottom: ".5rem",
              color: "var(--text-1)",
            }}
          >
            Password updated!
          </h3>
          <p
            style={{
              fontSize: ".9375rem",
              color: "var(--text-2)",
              lineHeight: 1.65,
              marginBottom: "1.75rem",
            }}
          >
            Your password has been changed successfully.
            <br />
            You can now sign in with your new password.
          </p>
          <button className="btn btn-primary auth-submit-btn" onClick={onBack}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            Sign In
          </button>
        </div>
      )}
    </>
  );
}

function EmailVerificationPanel({ email, onBack, onVerified, notify }) {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, startCountdown] = useCountdown(60);

  useEffect(() => {
    startCountdown(60);
  }, [startCountdown]);

  const verifyEmail = async () => {
    setError("");
    const code = otp.replace(/\D/g, "");
    if (code.length < 6)
      return setError("Please enter the complete 6-digit code.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-email", {
        email,
        otp: code,
      });
      notify("Email verified. Welcome to Quantum Logics.");
      onVerified(data.user, data.token);
      if (data.user.role === "admin") navigate("/admin-panel");
      else navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed.");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/resend-verification", { email });
      notify("New verification code sent.");
      startCountdown(60);
    } catch (err) {
      const retryAfter = err.response?.data?.retryAfter;
      if (retryAfter) startCountdown(retryAfter);
      setError(err.response?.data?.message || "Failed to resend code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-3)",
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: ".8125rem",
          fontWeight: 500,
          padding: "0 0 1rem 0",
          fontFamily: "var(--body)",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to sign in
      </button>

      <div className="auth-form-header">
        <h3 className="auth-form-title">Verify your email</h3>
        <p className="auth-form-sub">
          We sent a 6-digit code to{" "}
          <strong style={{ color: "var(--text-1)" }}>{email}</strong>.
          <br />
          Enter it below to activate your account.
        </p>
      </div>

      <OtpInput value={otp} onChange={setOtp} disabled={loading} />

      {error && (
        <div className="auth-error" style={{ marginBottom: ".875rem" }}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      <button
        className="btn btn-primary auth-submit-btn"
        onClick={verifyEmail}
        disabled={loading || otp.replace(/\D/g, "").length < 6}
      >
        {loading ? (
          <span className="auth-spinner" />
        ) : (
          <>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Verify Email
          </>
        )}
      </button>

      <div
        style={{
          textAlign: "center",
          marginTop: ".875rem",
          fontSize: ".8125rem",
          color: "var(--text-3)",
        }}
      >
        Didn't receive it?{" "}
        {countdown > 0 ? (
          <span style={{ color: "var(--text-2)", fontWeight: 600 }}>
            Resend in {countdown}s
          </span>
        ) : (
          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--blue)",
              fontWeight: 600,
              fontSize: ".8125rem",
              fontFamily: "var(--body)",
              padding: 0,
            }}
            onClick={resendCode}
            disabled={loading}
          >
            Resend code
          </button>
        )}
      </div>

      <div
        style={{
          marginTop: "1rem",
          padding: ".75rem .875rem",
          borderRadius: 10,
          background: "rgba(79,142,247,0.08)",
          border: "1px solid rgba(79,142,247,0.18)",
          color: "var(--text-2)",
          fontSize: ".78rem",
          lineHeight: 1.6,
        }}
      >
        Tip: if the email is delayed, check your spam folder and
        Promotions/Updates too. The code expires after 10 minutes.
      </div>
    </>
  );
}

function GooglePasswordPanel({
  googleSignup,
  onBack,
  onComplete,
  notify,
}) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError("");
    if (password.length < 6)
      return setError("Password must be at least 6 characters.");
    if (password !== confirmPassword)
      return setError("Passwords do not match.");

    setLoading(true);
    try {
      const { data } = await api.post("/auth/google/complete", {
        signupToken: googleSignup.signupToken,
        password,
      });
      notify("Quantum password set. Welcome to Quantum Logics.");
      onComplete(data.user, data.token);
      if (data.user.role === "admin") navigate("/admin-panel");
      else navigate("/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Could not complete Google signup. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <>
      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-3)",
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: ".8125rem",
          fontWeight: 500,
          padding: "0 0 1rem 0",
          fontFamily: "var(--body)",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <div className="auth-form-header">
        <h3 className="auth-form-title">Add a Quantum password</h3>
        <p className="auth-form-sub">
          Google confirmed{" "}
          <strong style={{ color: "var(--text-1)" }}>
            {googleSignup.profile.email}
          </strong>
          . Set a password for your Quantum Logics account.
        </p>
      </div>

      <div className="auth-google-profile">
        <div className="auth-google-avatar">
          {googleSignup.profile.name?.slice(0, 1).toUpperCase() || "G"}
        </div>
        <div>
          <strong>{googleSignup.profile.name}</strong>
          <span>{googleSignup.profile.email}</span>
        </div>
      </div>

      <div className="auth-form-fields">
        <div className="form-group">
          <label className="form-label">Quantum Password</label>
          <input
            className="form-input"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKey}
            autoFocus
            autoComplete="new-password"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Confirm Password</label>
          <input
            className="form-input"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={handleKey}
            autoComplete="new-password"
          />
        </div>

        {error && (
          <div className="auth-error">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        <button
          className="btn btn-primary auth-submit-btn"
          onClick={submit}
          disabled={loading}
        >
          {loading ? <span className="auth-spinner" /> : "Create Account"}
        </button>
      </div>
    </>
  );
}

// ── Main AuthModal ─────────────────────────────────────────────────────────────
export default function AuthModal({ mode, onClose, onLogin, setMode, notify }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [googleSignup, setGoogleSignup] = useState(null);
  const googleButtonRef = useRef(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const completeLogin = useCallback(
    (user, token) => {
      onLogin(user, token);
      if (user.role === "admin") navigate("/admin-panel");
      else navigate("/dashboard");
    },
    [navigate, onLogin],
  );

  const handleGoogleCredential = useCallback(
    async ({ credential }) => {
      setError("");
      setLoading(true);
      try {
        const { data } = await api.post("/auth/google", { credential });
        if (data.needsQuantumPassword) {
          setGoogleSignup({
            signupToken: data.signupToken,
            profile: data.profile,
          });
          notify("Google confirmed your email. Set a Quantum password.");
          return;
        }
        completeLogin(data.user, data.token);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Google sign-in failed. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    },
    [completeLogin, notify],
  );

  useEffect(() => {
    if (!googleClientId || showForgot || verifyEmail || googleSignup) return;

    let cancelled = false;
    loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !googleButtonRef.current) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleCredential,
          auto_select: false,
        });
        requestAnimationFrame(() => {
          if (cancelled || !googleButtonRef.current) return;
          googleButtonRef.current.innerHTML = "";
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            type: "standard",
            theme: "filled_black",
            size: "large",
            shape: "pill",
            text: mode === "login" ? "signin_with" : "continue_with",
            logo_alignment: "left",
            width: googleButtonRef.current.clientWidth,
          });
        });
      })
      .catch(() => {
        if (!cancelled) setError("Could not load Google sign-in.");
      });

    return () => {
      cancelled = true;
    };
  }, [
    googleClientId,
    googleSignup,
    handleGoogleCredential,
    mode,
    showForgot,
    verifyEmail,
  ]);

  const submit = async () => {
    setError("");
    const isDirectAdmin = isDirectAdminCredentials(form.email, form.password);

    if (mode === "register" && !isDirectAdmin) {
      if (!form.name || !form.email || !form.password)
        return setError("All fields are required.");
      if (form.password !== form.confirmPassword)
        return setError("Passwords do not match.");
      if (form.password.length < 6)
        return setError("Password must be at least 6 characters.");
    } else {
      if (!form.email || !form.password)
        return setError("Email and password are required.");
    }
    setLoading(true);
    try {
      const endpoint =
        mode === "register" && !isDirectAdmin ? "/auth/register" : "/auth/login";
      const payload =
        mode === "register" && !isDirectAdmin
          ? { name: form.name, email: form.email, password: form.password }
          : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      if (data.needsEmailVerification) {
        if (isDirectAdmin) {
          setError("Admin login is not configured on the server yet.");
          return;
        }
        setVerifyEmail(data.email || form.email.trim());
        notify(data.message || "Verification code sent to your email.");
        return;
      }
      completeLogin(data.user, data.token);
    } catch (err) {
      if (
        !isDirectAdmin &&
        ((mode === "login" &&
          err.response?.status === 403 &&
          err.response?.data?.code === "EMAIL_NOT_VERIFIED") ||
          (mode === "register" &&
            err.response?.status === 409 &&
            err.response?.data?.code === "EMAIL_PENDING_VERIFICATION"))
      ) {
        setVerifyEmail(form.email.trim());
      }
      setError(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") submit();
  };

  const eyeIcon = (visible) =>
    visible ? (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ) : (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="auth-modal">
        {/* Left: branding (always shown) */}
        <div className="auth-modal-brand">
          <div className="auth-brand-top">
            <div className="auth-brand-logo">
              <img
                src="/logo.png"
                alt="Quantum Logics"
                width="30"
                height="30"
                style={{ borderRadius: 8, objectFit: "contain" }}
              />
              <span className="auth-brand-name">Quantum Logics</span>
            </div>
          </div>
          <div className="auth-brand-body">
            <h2 className="auth-brand-h2">
              Build the future
              <br />
              with us.
            </h2>
            <p className="auth-brand-p">
              Join engineers and teams using the Quantum Logics ecosystem.
            </p>
            <div className="auth-brand-items">
              {[
                { icon: "⚡", text: "Quantum Language — write faster code" },
                { icon: "🎥", text: "QuantumMeet — collaborate in real time" },
                { icon: "📄", text: "DocsSyncer — always up-to-date docs" },
              ].map((i) => (
                <div key={i.text} className="auth-brand-item">
                  <span className="auth-brand-item-icon">{i.icon}</span>
                  <span>{i.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="auth-brand-footer">
            <div className="auth-brand-avatars">
              {["ZA", "SM", "HA", "NK"].map((i, idx) => (
                <div
                  key={i}
                  className="auth-brand-avatar"
                  style={{ zIndex: 4 - idx }}
                >
                  {i}
                </div>
              ))}
            </div>
            <span className="auth-brand-social-proof">
              Join 45+ team members
            </span>
          </div>
        </div>

        {/* Right: form panel */}
        <div className="auth-modal-form-panel">
          <button className="modal-close" onClick={onClose}>
            <Icon name="x" size={15} />
          </button>

          {/* ── Forgot password flow ── */}
          {googleSignup ? (
            <GooglePasswordPanel
              googleSignup={googleSignup}
              notify={notify}
              onComplete={onLogin}
              onBack={() => {
                setGoogleSignup(null);
                setError("");
              }}
            />
          ) : showForgot ? (
            <ForgotPasswordPanel
              onBack={() => {
                setShowForgot(false);
                setMode("login");
              }}
              notify={notify}
            />
          ) : verifyEmail ? (
            <EmailVerificationPanel
              email={verifyEmail}
              notify={notify}
              onVerified={onLogin}
              onBack={() => {
                setVerifyEmail("");
                setMode("login");
                setError("");
              }}
            />
          ) : (
            /* ── Login / Register flow ── */
            <>
              <div className="auth-form-header">
                <h3 className="auth-form-title">
                  {mode === "login" ? "Welcome back" : "Create your account"}
                </h3>
                <p className="auth-form-sub">
                  {mode === "login"
                    ? "Sign in to your Quantum Logics account."
                    : "Start your journey with Quantum Logics."}
                </p>
              </div>

              {googleClientId && (
                <>
                  <div
                    className="auth-google-button"
                    ref={googleButtonRef}
                    aria-label="Continue with Google"
                  />
                  <div className="auth-divider">
                    <span>or</span>
                  </div>
                </>
              )}

              <div className="auth-form-fields" key={mode}>
                {mode === "register" && (
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input
                      className="form-input"
                      name="ql-register-name"
                      placeholder="Muhammad Saad"
                      value={form.name}
                      onChange={set("name")}
                      onKeyDown={handleKey}
                      autoFocus
                      autoComplete="name"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    className="form-input"
                    type="email"
                    name={mode === "login" ? "ql-login-email" : "ql-register-email"}
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={set("email")}
                    onKeyDown={handleKey}
                    autoFocus={mode === "login"}
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="none"
                    autoComplete={mode === "login" ? "username" : "email"}
                  />
                </div>
                <div className="form-group">
                  <label
                    className="form-label"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>Password</span>
                    {mode === "login" && (
                      <button
                        type="button"
                        onClick={() => setShowForgot(true)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--blue)",
                          fontSize: ".78rem",
                          fontWeight: 600,
                          fontFamily: "var(--body)",
                          padding: 0,
                          letterSpacing: 0,
                          textTransform: "none",
                        }}
                      >
                        Forgot password?
                      </button>
                    )}
                  </label>
                  <div className="form-input-wrap">
                    <input
                      className="form-input"
                      type={showPass ? "text" : "password"}
                      name={
                        mode === "login" ? "ql-login-password" : "ql-new-password"
                      }
                      placeholder="••••••••"
                      value={form.password}
                      onChange={set("password")}
                      onKeyDown={handleKey}
                      style={{ paddingRight: "42px" }}
                      autoComplete={
                        mode === "login" ? "current-password" : "new-password"
                      }
                    />
                    <button
                      type="button"
                      className="form-eye-btn"
                      onClick={() => setShowPass((p) => !p)}
                      tabIndex={-1}
                    >
                      {eyeIcon(showPass)}
                    </button>
                  </div>
                </div>
                {mode === "register" && (
                  <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <input
                      className="form-input"
                      type="password"
                      name="ql-confirm-new-password"
                      placeholder="••••••••"
                      value={form.confirmPassword}
                      onChange={set("confirmPassword")}
                      onKeyDown={handleKey}
                      autoComplete="new-password"
                    />
                  </div>
                )}

                {error && (
                  <div className="auth-error">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                  </div>
                )}

                <button
                  className="btn btn-primary auth-submit-btn"
                  onClick={submit}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="auth-spinner" />
                  ) : mode === "login" ? (
                    "Sign In"
                  ) : (
                    "Create Account"
                  )}
                </button>
              </div>

              <div className="auth-switch">
                {mode === "login" ? (
                  <>
                    Don't have an account?{" "}
                    <button
                      className="auth-switch-btn"
                      onClick={() => setMode("register")}
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button
                      className="auth-switch-btn"
                      onClick={() => setMode("login")}
                    >
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
